import { useEffect, useRef, useState } from 'react';
import cm from 'classnames';

import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { prettyDate } from 'components/app/_helpers/_app.helper';
import { updateFileMediaOriginDate } from 'components/app/_mediator/app-files.slice';
import { TIME_ZONE_OPTIONS } from '../_mediator/projects.params';
import { setDateEditor } from '../_mediator/projects.slice';

import { ReactComponent as EditIcon } from 'assets/ui/edit.svg';
import { ReactComponent as CalendarIcon } from 'assets/calendar.svg';
import { Button } from 'components/UI/button/button';
import { Select } from 'components/UI/select/select';
import styles from './_elements.module.scss';

type Props = {
  fileDate: string | null; // ISO
  fileId: FileId;
};

export const DateCalendar = ({ fileDate, fileId }: Props) => {
  const selectedFile = useAppSelector((state) => state.projects.selectedFile);
  const dateEditor = useAppSelector((state) => state.projects.dateEditor);
  const calendarRef = useRef<HTMLInputElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);
  const dateFooterRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const [isOpenCalendar, setIsOpenCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState<string>('');
  const [timezoneOffset, setTimezoneOffset] = useState(0);

  const onClickCalendar = () => {
    if (isOpenCalendar) calendarRef.current?.blur();
    else calendarRef.current?.showPicker();
    setIsOpenCalendar(!isOpenCalendar);
  };

  const onSubmit = () => {
    let offset = Math.abs(timezoneOffset).toString();
    offset = offset.toString().padStart(2, '0');
    offset = `${timezoneOffset < 0 ? '-' : '+'}${offset}:00`;
    dispatch(updateFileMediaOriginDate({ mediaFileId: fileId, mediaOriginDate: calendarDate + offset }));
    dispatch(setDateEditor(null));
  };

  const onClickOutside = (e: MouseEvent) => {
    if (!dateRef.current) return;
    const target = e.target as HTMLElement;
    if (dateRef.current.contains(target)) return;
    if (target && target.closest('[data-icon]')) return;
    dispatch(setDateEditor(null));
    setIsOpenCalendar(false);
  };

  useEffect(() => {
    if (fileId !== dateEditor) return;
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [dateEditor]);

  useEffect(() => {
    if (!fileDate) return;
    if (dateEditor) {
      setTimezoneOffset(formatOffset(fileDate));
      setCalendarDate(formatIsoDate(fileDate));
    } else {
      setIsOpenCalendar(false);
      setTimezoneOffset(0);
      setCalendarDate('');
    }
  }, [dateEditor]);

  useEffect(() => {
    if (dateEditor) dateFooterRef.current?.scrollIntoView();
  }, [dateEditor]);

  return (
    <div className={styles.DateCalendar}>
      {dateEditor !== fileId ? (
        <div
          className={cm(styles.Date, {
            [styles['Date--selected']]: selectedFile === fileId,
          })}
          onClick={() => selectedFile === fileId && dispatch(setDateEditor(fileId))}
        >
          {prettyDate(fileDate) || '-'}
          <div data-icon className={styles.Date__ico} onClick={() => dispatch(setDateEditor(fileId))}>
            <EditIcon />
          </div>
        </div>
      ) : (
        <div className={styles.Calendar} ref={dateRef}>
          <main>
            <input
              className={styles.Calendar__input}
              value={calendarDate}
              type="datetime-local"
              step="0.001"
              onChange={(e) => setCalendarDate(e.target.value)}
            />
            <button className={styles.Calendar__ico} onClick={onClickCalendar} onBlur={() => setIsOpenCalendar(false)}>
              <CalendarIcon />
            </button>
            <Select
              className={styles.Calendar__offset}
              options={TIME_ZONE_OPTIONS}
              onChange={(e) => setTimezoneOffset(parseInt(e.target.value, 10))}
              value={timezoneOffset}
            />
            <input
              ref={calendarRef}
              className={styles.Calendar__picker}
              value={calendarDate}
              type="datetime-local"
              step="0.001"
              onChange={(e) => setCalendarDate(e.target.value)}
            />
          </main>
          <footer ref={dateFooterRef}>
            <Button onClick={() => dispatch(setDateEditor(null))}>Cancel</Button>
            <Button
              onClick={onSubmit}
              disabled={
                fileDate
                  ? calendarDate === formatIsoDate(fileDate) && timezoneOffset === formatOffset(fileDate)
                  : !calendarDate
              }
            >
              Save
            </Button>
          </footer>
        </div>
      )}
    </div>
  );
};

/* H E L P E R S */

export const formatIsoDate = (isoString: string) => {
  const regex = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(\.\d+)?([+-]\d{2}:\d{2})?$/;
  const match = isoString?.match(regex);
  if (!match) return '';
  const [, datePart, timePart, msPart = ''] = match;
  return `${datePart}T${timePart}${msPart}`;
};

export const formatOffset = (date: string): number => {
  const raw = date?.slice(-6, -3);
  return parseInt(raw, 10);
};
